import { useTranslation } from 'react-i18next';

import { Font, View } from '@react-pdf/renderer';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { ChartPdfImagesGroup } from 'PDF/components/Charts/ChartPdfImagesGroup';
import { PDFPage } from 'PDF/components/Page';
import { PdfContentText } from 'PDF/components/PdfContentText';
import { ITextItem } from 'PDF/components/PdfContentText';
import { StepsList } from 'PDF/components/StepsList';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import { RaisingAndAllocatingFundsSectionAvailability } from 'PDF/types/pdfSections';

import { IRaisingAndAllocatingFundsPdfData } from './types';
import { SectionCoverPagePdf } from '../SectionCoverPage';

Font.getRegisteredFonts();
Font.register(Noto);

export interface IRaisingAndAllocatingFundsPdfProps {
  magazineItems: ITextItem[][];
  data: IRaisingAndAllocatingFundsPdfData;
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const RaisingAndAllocatingFundsPdf = (
  props: IRaisingAndAllocatingFundsPdfProps & {
    availability: RaisingAndAllocatingFundsSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    data,
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage
  } = props;

  const { t } = useTranslation();

  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS, isExamplePage);
  const isSectionEmpty = !availability.wholePage;
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS)}
        background={sectionPicture ?? ''}
        highlightSettings={{
          position: 1,
          variant: 'after',
        }}
        subChapterNames={[]}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const { fundingStrategyImage, projectCostAndFundingNeedsImages, projectCostAndFundingNeedsSteps } = data;

  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
  const filteredProjectCostAndFundingNeedsSteps = isInvestor
    ? projectCostAndFundingNeedsSteps.filter(
        ({ trendName, trendCharacteristics }) =>
          (trendName || trendCharacteristics) && trendName !== t('pdfPages.mappings.rasingAndAllocating.loanAmount'),
      )
    : projectCostAndFundingNeedsSteps;
  const isProjectCostAndFundingNeedsImages = projectCostAndFundingNeedsImages.length > 0;
  const isProjectCostAndFundingNeedsSteps = filteredProjectCostAndFundingNeedsSteps.length > 0;
  const isProjectCostAndFundingNeeds = isProjectCostAndFundingNeedsImages && isProjectCostAndFundingNeedsSteps;
  const isFundingStrategyImageEmpty =
    fundingStrategyImage.length === 0 ||
    (fundingStrategyImage.length === 1 && fundingStrategyImage[0].src === 'emptyChart');

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.RAISING_AND_ALLOCATING_FUNDS)}
          background={sectionPicture ?? ''}
          highlightSettings={{
            position: 1,
            variant: 'after',
          }}
          subChapterNames={[]}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          <PdfContentText items={magazineItems[0]} withoutBottomMargin={isFundingStrategyImageEmpty} />
          <ChartPdfImagesGroup images={fundingStrategyImage} />
          <PdfContentText items={magazineItems[1]} />
          {isProjectCostAndFundingNeedsImages && (
            <ChartPdfImagesGroup images={projectCostAndFundingNeedsImages} headingProps={{ withBottomMargin: false }} />
          )}
          {isProjectCostAndFundingNeedsSteps && <StepsList list={filteredProjectCostAndFundingNeedsSteps} />}
          {!isProjectCostAndFundingNeeds && <View />}
          <PdfContentText items={magazineItems[2]} />
        </PDFPage>
      )}
    </>
  );
};
